/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/mv_sort.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Sorts a multivalued field in lexicographical order.
 *
 * @example
 * ROW a = [4, 2, -3, 2]
 * | EVAL sa = mv_sort(a), sd = mv_sort(a, "DESC")
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'mv_sort',
  signatures: [
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'ip',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'ip',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'unsigned_long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'unsigned_long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'unsigned_long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'version',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'version',
    },
    {
      params: [
        {
          name: 'field',
          type: 'version',
          optional: false,
        },
        {
          name: 'order',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
            allowedValues: [
              'asc',
              'desc',
            ],
          },
        },
      ],
      variadic: false,
      returnType: 'version',
    },
  ],
  preview: false,
  snapshotOnly: false,
};

export default definition;
